import { useEffect, useRef, useState } from "react";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import Input from "../../../../Components/Common/Input/Input";
import Button from "../../../../Components/Common/Button/Button";
import { getInspectors } from "../../../../services/inspectorAssignService";

interface Filters {
  inspector: string;
  type: string;
  status: string;
}

interface FilterOptions {
  type: string[];
  status: string[];
}

interface ProjectsToolbarProps {
  search: string;
  filters: Filters;
  options: FilterOptions;
  onSearch: (value: string) => void;
  onChangeFilter: (key: keyof Filters, value: string) => void;
  onChangeInspector: (name: string, id?: string) => void;
}

interface DropdownSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

const DropdownSelect = ({ value, options, onChange }: DropdownSelectProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="customDropdown" ref={ref}>
      <Button
        className={`customDropdownTrigger${open ? " open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="customDropdownValue">{value}</span>
        <span className={`customDropdownChevron${open ? " up" : ""}`}>›</span>
      </Button>
      {open && (
        <div className="customDropdownMenu">
          {options.map((option) => (
            <Button
              key={option}
              className={`customDropdownItem${value === option ? " selected" : ""}`}
              onClick={() => { onChange(option); setOpen(false); }}
            >
              {option}
              {value === option && <span className="customDropdownCheck">✓</span>}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

interface InspectorDropdownSelectProps {
  value: string;
  onChange: (name: string, id?: string) => void;
}

const InspectorDropdownSelect = ({ value, onChange }: InspectorDropdownSelectProps) => {
  const [open, setOpen] = useState(false);
  const [inspectors, setInspectors] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(false);

  const loadPage = async (pageNum: number, reset = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoading(true);
    try {
      const res: any = await getInspectors(pageNum, 10);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const totalPages: number = res?.data?.pagination?.totalPages ?? 1;
      setInspectors((prev) => (reset ? data : [...prev, ...data]));
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch {
    } finally {
      fetchingRef.current = false;
      if (reset) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setInspectors([]);
    setHasMore(false);
    loadPage(1, true);
  }, [open]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingRef.current) {
          loadPage(page + 1, false);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, page]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const renderStaticItem = (label: string) => (
    <Button
      key={label}
      className={`customDropdownItem${value === label ? " selected" : ""}`}
      onClick={() => { onChange(label, undefined); setOpen(false); }}
    >
      {label}
      {value === label && <span className="customDropdownCheck">✓</span>}
    </Button>
  );

  return (
    <div className="customDropdown" ref={ref}>
      <Button
        className={`customDropdownTrigger${open ? " open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="customDropdownValue">{value}</span>
        <span className={`customDropdownChevron${open ? " up" : ""}`}>›</span>
      </Button>
      {open && (
        <div className="customDropdownMenu">
          {renderStaticItem("All Inspectors")}

          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="customDropdownItem customDropdownSkeleton">
                  <Skeleton width="60%" height={12} />
                </div>
              ))
            : inspectors.map((insp) => {
                const name = `${insp.firstName} ${insp.lastName}`.trim();
                return (
                  <Button
                    key={insp.id}
                    className={`customDropdownItem${value === name ? " selected" : ""}`}
                    onClick={() => { onChange(name, insp.id); setOpen(false); }}
                  >
                    {name}
                    {value === name && <span className="customDropdownCheck">✓</span>}
                  </Button>
                );
              })}

          {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}

          {renderStaticItem("Unassigned")}
        </div>
      )}
    </div>
  );
};

const ProjectsToolbar = ({
  search,
  filters,
  options,
  onSearch,
  onChangeFilter,
  onChangeInspector,
}: ProjectsToolbarProps) => {
  return (
    <div className="toolbar">
      <div className="searchWrap">
        <Input
          className="searchInput"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Search projects..."
        />
      </div>

      <InspectorDropdownSelect
        value={filters.inspector}
        onChange={onChangeInspector}
      />

      <DropdownSelect
        value={filters.type}
        options={options.type}
        onChange={(val) => onChangeFilter("type", val)}
      />

      <DropdownSelect
        value={filters.status}
        options={options.status}
        onChange={(val) => onChangeFilter("status", val)}
      />
    </div>
  );
};

export default ProjectsToolbar;
